import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logo-big.png";
import BellImg from "../../assets/Bell.png";
import UserImg from "../../assets/User.png";
import iconInfo from "../../assets/icon-info.png";
import iconPhone from "../../assets/phone.png";
import AIChat from "../AIChat";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);

  // Get user data from localStorage (in a real app, you'd use a state management solution)
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/auth/signin");
  };

  const sidebarItems = [
    {
      id: "profile",
      label: "Your Profile",
      href: "/dashboard/profile",
      active:
        location.pathname.startsWith("/dashboard/profile") ||
        location.pathname === "/profile",
    },
    {
      id: "registration",
      label: "Your Registration",
      href: "/dashboard/registration",
      active: location.pathname.startsWith("/dashboard/registration"),
    },
    {
      id: "team",
      label: "Your Team",
      href: "/dashboard/team",
      active: location.pathname.startsWith("/dashboard/team"),
    },
    {
      id: "payment",
      label: "Your Payment",
      href: "/dashboard/payment",
      active: location.pathname.startsWith("/dashboard/payment"),
    },
    {
      id: "competitions",
      label: "Your Competitions",
      href: "/dashboard/competitions",
      active: location.pathname.startsWith("/dashboard/competitions"),
    },
    {
      id: "faq",
      label: "FAQ",
      href: "/dashboard/faq",
      active: location.pathname.startsWith("/dashboard/faq"),
    },
  ];

  // Get the current page title based on the active route
  const getCurrentPageTitle = () => {
    const activeItem = sidebarItems.find((item) => item.active);
    return activeItem ? activeItem.label : "Dashboard";
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile Header - Only visible on mobile */}
      <div className="lg:hidden bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Hamburger Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 touch-manipulation"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>

          {/* Mobile Logo */}
          <div className="flex-1 flex justify-center">
            <img src={logo} alt="Vietnam Young Lions Logo" className="h-8" />
          </div>

          {/* Mobile User Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAIChat(true)}
              className="w-12 h-12 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center touch-manipulation"
            >
              <span className="text-lg">✨</span>
            </button>
            <div className="relative group">
              <button className="p-2 rounded-md text-gray-600 hover:text-gray-900 touch-manipulation">
                <img src={UserImg} alt="User Profile" className="w-5 h-5" />
              </button>
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <Link
                  to="/dashboard/change-password"
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
                >
                  Change Password
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg border-t border-gray-100"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Desktop Sidebar - Hidden on mobile */}
        <div className="hidden lg:block w-80 bg-white flex-col min-h-screen">
          {/* Logo Section */}
          <div className="px-4 py-4">
            <div className="flex items-center gap-3">
              <img
                src={logo}
                alt="Vietnam Young Lions Logo"
                className="w-full"
              />
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 px-6 py-6 border-t border-gray-400">
            <ul className="space-y-1">
              {sidebarItems.map((item) => (
                <li key={item.id}>
                  <Link
                    to={item.href}
                    className={`block px-4 py-3 text-sm font-medium rounded-none transition-colors ${
                      item.active
                        ? "text-gray-900 font-semibold"
                        : "text-gray-400 hover:text-gray-700"
                    }`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Terms & Conditions Section */}
            <div className="mt-16 pt-6 border-t border-gray-400">
              <div className="w-full mb-4 text-center">
                <div className="w-full flex items-center justify-center mb-5">
                  <img src={iconInfo} className="w-10" />
                </div>
                <p className="text-sm text-[#403634] mb-4">
                  Please contact our hotline for support.
                </p>
                <div className="w-full flex items-center justify-center mb-5">
                  <img src={iconPhone} className="w-6" />
                </div>
                <p className="text-sm text-[#403634]">
                  +84 93 1333 150 (ext. 02)
                </p>
              </div>
            </div>
          </nav>
        </div>

        {/* Mobile Sidebar Overlay */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-40 flex">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Sidebar */}
            <div className="relative flex flex-col w-80 max-w-xs bg-white shadow-xl">
              {/* Logo Section */}
              <div className="px-4 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <img
                    src={logo}
                    alt="Vietnam Young Lions Logo"
                    className="h-8"
                  />
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-md text-gray-600 hover:text-gray-900 touch-manipulation"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Navigation Menu */}
              <nav className="flex-1 px-6 py-6 overflow-y-auto">
                <ul className="space-y-1">
                  {sidebarItems.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block px-4 py-3 text-sm font-medium rounded-none transition-colors touch-manipulation ${
                          item.active
                            ? "text-gray-900 font-semibold"
                            : "text-gray-400 hover:text-gray-700"
                        }`}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>

                {/* Terms & Conditions Section */}
                <div className="mt-8 pt-6 border-t border-gray-400">
                  <div className="w-full mb-4 text-center">
                    <div className="w-full flex items-center justify-center mb-3">
                      <img src={iconInfo} className="w-8" />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-3 text-sm">
                      Terms & Conditions
                    </h3>
                    <p className="text-xs text-[#403634] mb-3">
                      lease contact our hotline for support.
                    </p>
                    <div className="w-full flex items-center justify-center mb-3">
                      <img src={iconPhone} className="w-5" />
                    </div>
                    <p className="text-xs text-[#403634]">
                      +84 93 1333 150 (ext. 02)
                    </p>
                  </div>
                </div>
              </nav>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-screen">
          {/* Desktop Header - Hidden on mobile */}
          <header className="hidden lg:block bg-white px-8 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-semibold text-gray-900">
                {getCurrentPageTitle()}
              </h1>

              <div className="flex items-center gap-6">
                <div className="text-right">
                  <div className="text-sm text-gray-900">
                    Welcome,{" "}
                    <span className="font-semibold">
                      {user.full_name || "User"}
                    </span>
                  </div>
                </div>

                {/* Notification Icon */}
                <button
                  onClick={() => setShowAIChat(true)}
                  className="w-12 h-12 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center touch-manipulation"
                >
                  <span className="text-lg">✨</span>
                </button>

                {/* Profile Icon with Dropdown */}
                <div className="relative group">
                  <button className="p-2">
                    <img src={UserImg} alt="User Profile" className="w-5 h-5" />
                  </button>

                  {/* Dropdown menu */}
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                    <Link
                      to="/dashboard/change-password"
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
                    >
                      Change Password
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg border-t border-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Mobile Page Title */}
          <div className="lg:hidden bg-white px-4 py-3 border-b border-gray-200">
            <h1 className="text-lg font-semibold text-gray-900">
              Your Profile
            </h1>
          </div>

          {/* Main Content */}
          <main className="flex-1 p-4 lg:p-8">{children}</main>
          {/* AI Chat Popup */}
          <AIChat isOpen={showAIChat} onClose={() => setShowAIChat(false)} />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
